import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card"
import { Alert, AlertDescription } from "../ui/alert"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select"
import { Eye, EyeOff, Lock, Mail, Brain, Building2, ArrowLeft } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../contexts/auth-context"
import { apiService } from "../../lib/api"
import { formatErrorMessage } from "../../lib/utils"

// Brand colors from landing page and my-account
const BRAND_COLORS = {
  primary: '#c39d76', // Warm beige/tan
  secondary: '#d4a574', // Lighter tan
  dark: '#2C2416', // Dark brown
  darkGrey: '#3F454A', // Dark grey
  accent: '#dfc9b1', // Light beige
}

const loginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [showCompanySelection, setShowCompanySelection] = useState(false)
  const [companies, setCompanies] = useState<any[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("")
  const [loadingCompanies, setLoadingCompanies] = useState(false)
  const { login, refreshUser } = useAuth()
  const navigate = useNavigate()

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Helper function to decode JWT payload
  const parseJwt = (token: string) => {
    try {
      const base64Url = token.split('.')[1]
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(jsonPayload)
    } catch (error) {
      return null
    }
  }

  // Fetch companies after successful login
  const fetchUserCompanies = async () => {
    setLoadingCompanies(true)
    try {
      // Check if user is super_admin or admin from JWT token
      const token = localStorage.getItem('auth_token')
      let isSuperAdmin = false
      let isAdmin = false
      
      if (token) {
        const payload = parseJwt(token)
        const userRoles = payload?.roles || []
        isSuperAdmin = Array.isArray(userRoles) && userRoles.includes('super_admin')
        isAdmin = Array.isArray(userRoles) && userRoles.includes('admin')
      }

      // If user is super_admin, they can access all companies - skip selection and go to dashboard
      // If user is admin, they see their assigned companies - show selection if multiple
      if (isSuperAdmin) {
        // Optionally set first company if available, but not required for admins
        try {
          const response = await apiService.getCompanies({ page: 1, pageSize: 1 })
          const companiesArray = Array.isArray(response?.data) ? response.data : []
          
          // Set first company if available, but proceed anyway
          if (companiesArray.length > 0) {
            const company = companiesArray[0]
            localStorage.setItem("company_id", company.id)
            if (company.name) {
              localStorage.setItem("company_name", company.name)
            }
          }
        } catch (err) {
          // Proceeding without companies for admin
        }
        
        // Check if there's a return URL from booking flow
        const returnUrl = localStorage.getItem('bookingReturnUrl')
        if (returnUrl) {
          localStorage.removeItem('bookingReturnUrl')
          localStorage.removeItem('bookingRoomId')
          navigate(returnUrl)
        } else {
          navigate("/dashboard")
        }
        return
      }

      // For non-admin users, fetch companies and show selection
      const response = await apiService.getCompanies({ page: 1, pageSize: 100 })
      
      // Handle response structure
      const allCompanies = Array.isArray(response?.data) ? response.data : []
      
      // Filter out inactive companies - only show active companies for login
      const activeCompanies = allCompanies.filter((company: any) => {
        // Check status from company object or companySettings
        const status = company.status || 
                      company.companySettings?.find((s: any) => s.key === 'status')?.value ||
                      'active';
        return status === 'active' || status === 'ACTIVE';
      });
      
      setCompanies(activeCompanies)
      
      // If no active companies, show error
      if (activeCompanies.length === 0) {
        setError("You don't have access to any active companies. All your assigned companies appear to be inactive. Please contact your administrator to activate a company or assign you to an active company.")
        setIsLoading(false)
        return
      }
      
      // If only one active company, auto-select it
      if (activeCompanies.length === 1) {
        const company = activeCompanies[0]
        await handleCompanySelect(company.id, company.name, company)
      } else if (activeCompanies.length > 1) {
        // Show company selection
        setShowCompanySelection(true)
      } else {
        // No companies, proceed anyway
        // Check if there's a return URL from booking flow
        const returnUrl = localStorage.getItem('bookingReturnUrl')
        if (returnUrl) {
          localStorage.removeItem('bookingReturnUrl')
          localStorage.removeItem('bookingRoomId')
          navigate(returnUrl)
        } else {
          navigate("/dashboard")
        }
      }
    } catch (err: any) {
      const formattedError = formatErrorMessage(err)
      // Only show error if it's a network error, otherwise proceed silently
      if (formattedError.includes('No internet connection') || formattedError.includes('network')) {
        setError(formattedError)
        setIsLoading(false)
      } else {
        // Proceed to dashboard even if companies fetch fails for other errors
        // Check if there's a return URL from booking flow
        const returnUrl = localStorage.getItem('bookingReturnUrl')
        if (returnUrl) {
          localStorage.removeItem('bookingReturnUrl')
          localStorage.removeItem('bookingRoomId')
          navigate(returnUrl)
        } else {
          navigate("/dashboard")
        }
      }
    } finally {
      setLoadingCompanies(false)
    }
  }

  const handleCompanySelect = async (companyId: string, companyName?: string, companyObject?: any) => {
    // Try to find company in the list, or use the provided company object
    let selectedCompany = companyObject || companies.find(c => c.id === companyId)
    
    // If still not found, try to fetch it or proceed if it was just auto-selected
    if (!selectedCompany) {
      // If companies array is empty or still loading, this might be an auto-select scenario
      // Don't block - proceed with the selection if we have a valid companyId
      if (companyId) {
        try {
          localStorage.setItem("company_id", companyId)
          if (companyName) {
            localStorage.setItem("company_name", companyName)
          }
          
          // Refresh user state to get the correct role and accessScope for this company
          if (refreshUser) {
            await refreshUser()
          }
          
          // Check if there's a return URL from booking flow
          const returnUrl = localStorage.getItem('bookingReturnUrl')
          if (returnUrl) {
            localStorage.removeItem('bookingReturnUrl')
            localStorage.removeItem('bookingRoomId')
            navigate(returnUrl)
          } else {
            navigate("/dashboard")
          }
          return
        } catch (err) {
          setError("Unable to save your company selection. Please refresh the page and try again.")
          return
        }
      } else {
        setError("Unable to access the selected company. The company may have been deactivated or you may not have permission to access it. Please contact your administrator.")
        return
      }
    }
    
    // Double-check company status if we have the company object
    if (selectedCompany) {
      const status = selectedCompany.status || 
                     selectedCompany.companySettings?.find((s: any) => s.key === 'status')?.value ||
                     'active';
      
      if (status !== 'active' && status !== 'ACTIVE') {
        setError("This company is currently inactive and cannot be accessed. Please contact your administrator to activate the company.")
        return
      }
    }
    
    try {
      localStorage.setItem("company_id", companyId)
      if (companyName) {
        localStorage.setItem("company_name", companyName)
      }
      
      // Refresh user state to get the correct role and accessScope for this company
      if (refreshUser) {
        await refreshUser()
      }
      
      // Check if there's a return URL from booking flow
      const returnUrl = localStorage.getItem('bookingReturnUrl')
      if (returnUrl) {
        localStorage.removeItem('bookingReturnUrl')
        localStorage.removeItem('bookingRoomId')
        navigate(returnUrl)
      } else {
        navigate("/dashboard")
      }
    } catch (err) {
      setError("Unable to save your company selection. Please refresh the page and try again.")
    }
  }

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true)
    setError("")
    
    try {
      await login(data.email, data.password)
      // Fetch companies after successful login
      await fetchUserCompanies()
    } catch (err: any) {
      const errorMessage = err?.message || '';
      
      // Check if it's a user not found error
      const isUserNotFound = errorMessage.toLowerCase().includes('user not exist') || 
                            errorMessage.toLowerCase().includes('account not found') ||
                            errorMessage.toLowerCase().includes('user not found') ||
                            err?.response?.data?.error?.code === 'user_not_found';
      
      // Check if it's a login/authentication error (wrong password)
      const isLoginError = errorMessage.toLowerCase().includes('invalid') || 
                          errorMessage.toLowerCase().includes('password') ||
                          errorMessage.toLowerCase().includes('credentials') ||
                          errorMessage.toLowerCase().includes('unauthorized');
      
      if (isUserNotFound) {
        // Show clear message when account doesn't exist
        setError("User not exist. Please check your email or create a new account.")
      } else if (isLoginError) {
        // Show generic message for wrong password
        setError("Invalid email or password")
      } else {
        // For non-login errors (network, etc.), use formatted error
        const formattedError = formatErrorMessage(err)
        setError(formattedError)
      }
      setIsLoading(false)
    }
  }

  const handleCompanyContinue = async () => {
    if (!selectedCompanyId) {
      setError("Please select a company from the list to continue.")
      return
    }
    const selectedCompany = companies.find(c => c.id === selectedCompanyId)
    if (!selectedCompany) {
      setError("Unable to access the selected company. The company may have been deactivated or you may not have permission to access it. Please contact your administrator.")
      return
    }
    
    // Validate company is active before proceeding
    const status = selectedCompany.status || 
                   selectedCompany.companySettings?.find((s: any) => s.key === 'status')?.value ||
                   'active';
    
    if (status !== 'active' && status !== 'ACTIVE') {
      setError("This company is currently inactive and cannot be accessed. Please contact your administrator to activate the company.")
      return
    }
    
    await handleCompanySelect(selectedCompanyId, selectedCompany?.name)
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Black Overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/log-sin-bg.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-center text-white space-y-6">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-24 w-24 object-contain mx-auto mb-8"
            />
            <h1 className="text-4xl font-bold">Welcome Back</h1>
            <p className="text-lg text-gray-200">Join us and start your journey with ikoro</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-white relative">
        {/* Back to Home Link */}
        <Link
          to="/"
          className="absolute top-4 left-4 text-sm hover:underline font-medium flex items-center gap-1 z-10"
          style={{ color: BRAND_COLORS.primary }}
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back to Home</span>
        </Link>
        
        <div className="w-full max-w-md">
        <div className="text-center space-y-2 pb-4 pt-8 sm:pt-4">
          {/* Brand Logo - Compact */}
          <div className="mx-auto">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-16 w-16 object-contain mx-auto"
            />
          </div>
          <h1 className="text-xl font-bold" style={{ color: BRAND_COLORS.dark }}>
            Welcome to IKORO
          </h1>
        </div>
        
        <div className="space-y-3 px-3 sm:px-6 pb-6">
          {error && (
            <Alert variant="destructive" className="border-2 border-red-500 bg-red-50 py-3">
              <AlertDescription className="text-red-700 font-semibold text-sm flex items-center gap-2">
                <span className="text-red-600 text-lg">⚠️</span>
                <span>{error}</span>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Company Selection Step */}
          {showCompanySelection && (
            <div className="space-y-3">
              <div className="text-center space-y-1">
                <Building2 className="mx-auto w-6 h-6" style={{ color: BRAND_COLORS.primary }} />
                <CardTitle className="text-lg" style={{ color: BRAND_COLORS.dark }}>Select a Company</CardTitle>
                <CardDescription className="text-sm" style={{ color: BRAND_COLORS.darkGrey }}>
                  You have access to multiple companies. Please select one to continue.
                </CardDescription>
              </div>
              
              {loadingCompanies ? (
                <div className="text-center py-2">
                  <p className="text-sm" style={{ color: BRAND_COLORS.darkGrey }}>Loading companies...</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="company" className="text-sm">Company</Label>
                    <Select
                      value={selectedCompanyId}
                      onValueChange={setSelectedCompanyId}
                      disabled={loadingCompanies}
                    >
                      <SelectTrigger id="company" className="w-full border border-solid" style={{ borderColor: `${BRAND_COLORS.primary}40` }}>
                        <SelectValue placeholder="Select a company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name || company.id}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <Button
                    onClick={handleCompanyContinue}
                    className="w-full font-semibold"
                    disabled={!selectedCompanyId || loadingCompanies}
                    style={{
                      background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                      color: 'white',
                    }}
                  >
                    Continue
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {/* Login Form */}
          {!showCompanySelection && (
            <form 
              onSubmit={(e) => {
                e.preventDefault()
                handleLoginSubmit(onLoginSubmit)(e)
              }} 
              className="space-y-3"
            >
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    className={`pl-10 ${loginErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={loginErrors.email ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerLogin("email")}
                    disabled={isLoading}
                  />
                </div>
                {loginErrors.email && (
                  <p className="text-sm text-red-600 font-medium">{loginErrors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-sm">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    className={`pl-10 pr-10 ${loginErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
                    style={loginErrors.password ? {} : { borderColor: `${BRAND_COLORS.primary}40` }}
                    {...registerLogin("password")}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:opacity-70"
                    style={{ color: BRAND_COLORS.primary }}
                    disabled={isLoading}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {loginErrors.password && (
                  <p className="text-sm text-red-600 font-medium">{loginErrors.password.message}</p>
                )}
              </div>
              
              <Button
                type="submit"
                className="w-full font-semibold py-2.5"
                disabled={isLoading}
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                  color: 'white',
                }}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </Button>
              
              <div className="flex items-center justify-center space-x-3 pt-1">
                <Link
                  to="/auth/forgot-password"
                  className="text-xs hover:underline font-medium"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  Forgot password?
                </Link>
                <span className="text-xs" style={{ color: BRAND_COLORS.darkGrey }}>•</span>
                <Link
                  to="/register"
                  className="text-xs hover:underline font-medium"
                  style={{ color: BRAND_COLORS.primary }}
                >
                  Create account
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}
